import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { apiFetch } from "@/lib/api";
import { loadCalls } from "@/lib/calls";
import type { CurrentUser } from "@/lib/providers";

/**
 * Главная — дашборд: по плитке на раздел, клик ведёт в раздел. Спека
 * `docs/specs/2026-09-24-home-dashboard-design.md`.
 */

/** `GET /api/leads/new` — NEW с российским номером из своей копии Bitrix. */
type NewLeads = {
  count: number;
  no_phone: number;
  avg_min: number | null;
  oldest_at: string | null;
  last_sync: string | null;
};

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";

/** Сегодня по локальному календарю, ГГГГ-ММ-ДД — как у экрана «Звонки». */
const today = () => new Date().toLocaleDateString("sv-SE");

/** 135 → «2 ч 15 мин». */
function duration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h} ч ${m} мин` : `${m} мин`;
}

/** «ГГГГ-ММ-ДДTчч:мм…» (MSK без зоны) → «чч:мм», не сегодня — «ДД.ММ чч:мм». */
function clock(iso: string): string {
  const hm = iso.slice(11, 16);
  return iso.startsWith(today()) ? hm : `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${hm}`;
}

/** Минут с наивного MSK-времени до сейчас. */
const minutesSince = (iso: string) => (Date.now() - new Date(`${iso}+03:00`).getTime()) / 60_000;

function Tile(props: {
  to: string;
  title: string;
  annot: string;
  q: { isPending: boolean; isError: boolean };
  value: number | undefined;
  lines: ReactNode[] | undefined;
}) {
  const { q } = props;
  let value: ReactNode;
  if (q.isPending) value = <span className="text-fg-faint">…</span>;
  else if (q.isError || props.value === undefined) value = <span className="text-[14px] text-err">нет данных</span>;
  else value = <span className={props.value ? "text-err" : ""}>{props.value}</span>;
  return (
    <Link
      to={props.to}
      data-annot={props.annot}
      className={`${card} block w-72 p-4 transition-colors hover:bg-bg-hover`}
    >
      <div className="text-[12px] text-fg-muted">{props.title}</div>
      <div className="mt-1 text-[26px] font-semibold leading-none tabular-nums">{value}</div>
      {props.lines?.filter(Boolean).map((l, i) => (
        <div key={i} className={`${i ? "" : "mt-2"} text-[12px] text-fg-muted`}>
          {l}
        </div>
      ))}
    </Link>
  );
}

export function HomePage() {
  const { data: user } = useGetIdentity<CurrentUser>();
  const day = today();
  const leads = useQuery({
    queryKey: ["leads-new"],
    queryFn: () => apiFetch<NewLeads>("/api/leads/new"),
    refetchInterval: 60_000,
  });
  const calls = useQuery({
    queryKey: ["calls-report", day],
    queryFn: () => loadCalls(day),
    refetchInterval: 60_000,
    select: (r) => {
      const none = r.numbers.filter((n) => n.outcome === "none" && n.episode_from);
      const oldest = none.map((n) => n.episode_from!).sort()[0] ?? null;
      return { count: r.totals.not_called_back, oldest };
    },
  });
  const l = leads.data;
  const c = calls.data;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
      <div>
        <h1 className="text-[18px] font-semibold tracking-[-0.015em]">Прокат</h1>
        <p className="text-[13px] text-fg-muted">
          {user?.name ? `${user.name}, ` : ""}кабинет управления прокатом. Данные — из CRM ({user?.crm ?? "…"}).
        </p>
      </div>
      <section className="flex flex-wrap gap-3">
        <Tile
          to="/distribution"
          title="Необработанные лиды"
          annot="главная: необработанные лиды"
          q={leads}
          value={l?.count}
          lines={
            l && [
              <>
                в NEW в среднем{" "}
                <b className="font-semibold text-fg">{l.avg_min === null ? "—" : duration(l.avg_min)}</b>
              </>,
              l.oldest_at ? `самый давний — ${duration(minutesSince(l.oldest_at))}` : null,
              <span className="text-fg-faint">
                с российским номером · без номера ещё {l.no_phone} · синхр. {l.last_sync ? clock(l.last_sync) : "—"}
              </span>,
            ]
          }
        />
        <Tile
          to="/calls"
          title="Не перезвонили"
          annot="главная: не перезвонили"
          q={calls}
          value={c?.count}
          lines={
            c && [
              c.oldest ? (
                <>
                  самый старый пропуск —{" "}
                  <b className="font-semibold text-fg">{clock(c.oldest)}</b>, ждёт {duration(minutesSince(c.oldest))}
                </>
              ) : (
                "все пропущенные перезвонены"
              ),
              <span className="text-fg-faint">пропущенные номера за сегодня</span>,
            ]
          }
        />
      </section>
    </main>
  );
}
